import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Clock, Upload, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface HistoryEntry {
  id: string;
  operation_type: string;
  file_name: string | null;
  countries_opened: number;
  countries_closed: number;
  total_processed: number;
  errors_count: number;
  error_details: any;
  created_at: string;
}

const AdminImportExportHistory = () => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('destination_import_export_history')
        .select('*')
        .eq('company_id', '00000000-0000-0000-0000-000000000000') // Admin actions
        .in('operation_type', ['admin_import', 'admin_export'])
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center text-muted-foreground">
          <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>Aucun historique d'import/export</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Clock className="h-5 w-5" />
        Historique des imports/exports
      </h3>
      
      <div className="space-y-3">
        {history.map((entry) => (
          <div key={entry.id} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
            <div className="mt-1">
              {entry.operation_type === 'admin_import' ? (
                <Upload className="h-5 w-5 text-blue-600" />
              ) : (
                <Download className="h-5 w-5 text-green-600" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">
                  {entry.operation_type === 'admin_import' ? 'Import' : 'Export'}
                </span>
                {entry.file_name && (
                  <span className="text-sm text-muted-foreground truncate">
                    {entry.file_name}
                  </span>
                )}
              </div>
              
              <div className="text-sm text-muted-foreground space-y-1">
                <div className="flex flex-wrap gap-3">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    {entry.countries_opened} activés
                  </span>
                  <span className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3 text-blue-600" />
                    {entry.countries_closed} désactivés
                  </span>
                  {entry.errors_count > 0 && (
                    <span className="flex items-center gap-1 text-red-600">
                      <AlertCircle className="h-3 w-3" />
                      {entry.errors_count} erreurs
                    </span>
                  )}
                </div>
                <div className="text-xs">
                  {format(new Date(entry.created_at), "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default AdminImportExportHistory;
